import type { JSONContent } from '@tiptap/react';

import type { IMailPlaceholder } from '../../api/mail-template-contracts';

/**
 * The stored string of a template field ⇄ the document its editor holds. The API is plain text:
 * a placeholder is `{{name}}` or `{{name|fallback}}` — the words sent when the value is empty —
 * (spaces inside the braces and around the bar allowed), a blank line starts a paragraph
 * and a single `\n` is a line break. In the editor a placeholder is one atomic node — a pill —
 * whatever its name, so a name survey-api no longer knows still round-trips, drawn as unknown.
 * Type-only imports: the page reads this without loading the editor's chunk.
 */

/** Maily's variable node — `variable {id, fallback}` — so a field's document is already in the
 *  shape Maily's editor and renderer read (the templates roadmap's block editor). */
export const PLACEHOLDER_NODE = 'variable';

/** A placeholder as survey-api reads it, what is inside the braces captured. */
const TOKEN = /\{\{([^{}]*)\}\}/g;

/** The longest fallback survey-api takes. */
export const FALLBACK_MAX = 100;

/** A placeholder's inside — `name` or `name|fallback` — split and trimmed. */
function tokenParts(inside: string): { id: string; fallback: Nullable<string> } {
  const bar = inside.indexOf('|');
  if (bar < 0) return { id: inside.trim(), fallback: null };
  return { id: inside.slice(0, bar).trim(), fallback: inside.slice(bar + 1).trim() || null };
}

/** The stored token of a pill. */
export function tokenOf(id: string, fallback: Nullable<string>): string {
  return fallback ? `{{${id}|${fallback}}}` : `{{${id}}}`;
}

/** Why survey-api would refuse a fallback, or null. */
export function fallbackProblem(fallback: string): Nullable<string> {
  if (/[{}|]/.test(fallback)) return 'No {, } or | in a fallback.';
  if (fallback.trim().length > FALLBACK_MAX) return `At most ${FALLBACK_MAX} characters.`;
  return null;
}

/** A paragraph break in the stored string: a blank line. */
const PARAGRAPH_BREAK = /\n[ \t]*\n/;

/** What a field's editor offers the page: focus, and a placeholder put where the caret was. */
export interface IPlaceholderFieldHandle {
  focus: (where?: 'end') => void;
  /** Puts a placeholder where the caret was — in place of a `{{…` the field handed to the sheet. */
  insert: (name: string) => void;
  /** The sheet closed: a `{{…` handed to it stays as typed. */
  release?: () => void;
  /** A rich part's: resolves once its last edit is compiled and handed out. */
  flush?: () => Promise<void>;
  /** A rich part's: an edit typed and not yet compiled. */
  pending?: () => boolean;
  /** A body's: its portal block selected and scrolled to. */
  selectBlock?: (id: string) => void;
}

/** "submitted_at" → "Submitted at": the pill's words. */
export function placeholderLabel(name: string): string {
  const words = name.replace(/[_-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : name;
}

/** "survey_url" → "Survey URL": a name's words, its abbreviations capitalised. */
export function nameWords(name: string): string {
  return placeholderLabel(name).replace(/\b(cc|pdf|idp|url)\b/gi, (word) => word.toUpperCase());
}

/** A placeholder's short words — the pill's and the picker's first line; its description is the
 *  tooltip. survey-api's `label` is a list field's own label, but for a placeholder the mail fills
 *  it only repeats the description, so that one is named from its name. */
export function placeholderWords(placeholder: IMailPlaceholder): string {
  const own = Boolean(placeholder.field) || placeholder.label !== placeholder.description;
  return (own && placeholder.label) || nameWords(placeholder.name);
}

export function findPlaceholder(
  placeholders: readonly IMailPlaceholder[],
  name: string
): Nullable<IMailPlaceholder> {
  return placeholders.find((placeholder) => placeholder.name === name) ?? null;
}

/** One line of stored text as inline nodes: text and pills. */
function inlineOf(line: string): JSONContent[] {
  const nodes: JSONContent[] = [];
  let at = 0;
  for (const match of line.matchAll(TOKEN)) {
    if (match.index > at) nodes.push({ type: 'text', text: line.slice(at, match.index) });
    nodes.push({ type: PLACEHOLDER_NODE, attrs: tokenParts(match[1]) });
    at = match.index + match[0].length;
  }
  if (at < line.length) nodes.push({ type: 'text', text: line.slice(at) });
  return nodes;
}

function paragraphOf(text: string, multiline: boolean): JSONContent {
  const content = multiline
    ? text.split('\n').flatMap((line, index) => [
        ...(index > 0 ? [{ type: 'hardBreak' }] : []),
        ...inlineOf(line)
      ])
    : inlineOf(text.replace(/\s*\n\s*/g, ' '));
  return content.length > 0 ? { type: 'paragraph', content } : { type: 'paragraph' };
}

/** The paragraphs a stored value (or a pasted text) makes — always one for a single-line field. */
export function paragraphsOf(value: string, multiline: boolean): JSONContent[] {
  const text = value.replace(/\r\n?/g, '\n');
  return multiline
    ? text.split(PARAGRAPH_BREAK).map((paragraph) => paragraphOf(paragraph, true))
    : [paragraphOf(text, false)];
}

export function documentOf(value: string, multiline: boolean): JSONContent {
  return { type: 'doc', content: paragraphsOf(value, multiline) };
}

/** The editor's document as the string survey-api stores. */
export function stringOf(doc: JSONContent): string {
  return (doc.content ?? [])
    .map((paragraph) =>
      (paragraph.content ?? [])
        .map((node) => {
          if (node.type === 'text') return node.text ?? '';
          if (node.type === 'hardBreak') return '\n';
          if (node.type === PLACEHOLDER_NODE) {
            return tokenOf(String(node.attrs?.id ?? ''), (node.attrs?.fallback as Nullable<string>) ?? null);
          }
          return '';
        })
        .join('')
    )
    .join('\n\n');
}

/** A placeholder token in a run of stored text, for the diff and the history to draw as a pill. */
export function placeholderToken(token: string): Nullable<{ id: string; fallback: Nullable<string> }> {
  const match = /^\{\{([^{}]*)\}\}$/.exec(token);
  return match ? tokenParts(match[1]) : null;
}

/** The pill's words: the placeholder's, then its fallback — faint, cut when long. */
export function pillWords(
  known: boolean,
  id: string,
  fallback: Nullable<string>,
  /** The catalogue's words for it — a staff template's placeholders carry them. */
  words?: string
): { label: string; fallback: Nullable<string> } {
  const label = known ? (words ?? placeholderLabel(id)) : `Unknown: ${id || '(empty)'}`;
  if (!fallback) return { label, fallback: null };
  return { label, fallback: fallback.length > 24 ? `${fallback.slice(0, 23)}…` : fallback };
}

/** The faint fallback inside a pill. */
export const PILL_FALLBACK_CLASS = 'font-normal opacity-60';

/** The pill's look — shared by the editor's node, the palette and the diff. */
export function pillClass(known: boolean): string {
  return [
    'mx-px inline rounded-full border px-1.5 font-sans align-baseline text-[0.75rem] leading-[1.35] font-medium whitespace-nowrap! select-none',
    known
      ? 'border-[color-mix(in_srgb,var(--color-teal)_40%,white)] bg-[color-mix(in_srgb,var(--color-turquoise)_14%,white)] text-[var(--color-royal-blue)]'
      : 'border-[color-mix(in_srgb,var(--color-deep-magenta)_45%,white)] bg-[color-mix(in_srgb,var(--color-magenta)_10%,white)] text-[var(--color-deep-magenta)]'
  ].join(' ');
}

/** A placeholder's sample where it says more than its words — survey-api samples a text field by
 *  its own label — else empty. */
export function placeholderSample(placeholder: IMailPlaceholder): string {
  const { sample } = placeholder;
  return sample && sample !== placeholderWords(placeholder) && sample !== placeholder.label ? sample : '';
}

/** What a pill says on hover: its description and sample, or that survey-api does not know it. */
export function pillTitle(
  placeholder: Nullable<IMailPlaceholder>,
  name: string,
  fallback: Nullable<string> = null
): string {
  if (!placeholder) return `{{${name}}} is not a placeholder this email knows`;
  const when = fallback ? ` When empty: ${fallback}` : '';
  const sample = placeholderSample(placeholder);
  return `${placeholder.description}${sample ? ` — e.g. ${sample}` : ''}.${when}`;
}

/** The picker's first group: the placeholders the email fills itself. */
export const OWN_GROUP = 'This email';

/** Whether the email fills it itself rather than reading a list field: `own`, the group "This
 *  email", or — from a survey-api that sends neither — a Mail placeholder reading no field. */
export function isOwnPlaceholder(placeholder: IMailPlaceholder): boolean {
  return placeholder.own ?? (placeholder.group === OWN_GROUP || (!placeholder.field && (placeholder.group ?? 'Mail') === 'Mail'));
}
